/**
 * Formatting toolbar of the rich-text editor (DESIGN.md T11). One WAI-ARIA toolbar: a single tab
 * stop, arrow keys move between controls (roving tabindex), toggles expose `aria-pressed`, and
 * every control has a tooltip with its shortcut.
 */
import { useEditorState, type Editor } from '@tiptap/react';
import {
  ALargeSmallIcon,
  BaselineIcon,
  BoldIcon,
  ChevronDownIcon,
  HighlighterIcon,
  ImageIcon,
  ImagePlusIcon,
  IndentDecreaseIcon,
  IndentIncreaseIcon,
  ItalicIcon,
  LinkIcon,
  ListIcon,
  ListOrderedIcon,
  MoreHorizontalIcon,
  Redo2Icon,
  RemoveFormattingIcon,
  SeparatorHorizontalIcon,
  StrikethroughIcon,
  Table2Icon,
  TablePropertiesIcon,
  TextAlignCenterIcon,
  TextAlignEndIcon,
  TextAlignJustifyIcon,
  TextAlignStartIcon,
  TextQuoteIcon,
  TypeIcon,
  UnderlineIcon,
  Undo2Icon,
  type LucideIcon,
} from 'lucide-react';
import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Button } from '../components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/tooltip';
import { BREAKPOINTS, useMediaQuery } from '../hooks/use-media-query';
import { cn } from '../lib/utils';
import { PICTURE_PLACEMENTS, PICTURE_SIZES, type PicturePlacement, type PictureSizeId } from './extensions';
import { ALIGNMENTS, FONT_SIZES, HIGHLIGHT_COLORS, TEXT_COLORS, type Alignment, type ColorId, type RichTextEditorLabels } from './labels';
import { CELL_ALIGNMENTS, TABLE_BORDERS, TABLE_PADDINGS, type CellAlignment, type TableBorders, type TablePadding } from './table';

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

/** `Mod+Shift+K` → `Ctrl+Shift+K` (or `⌘⇧K` on macOS) for tooltips. */
export function shortcutLabel(chord: string): string {
  if (!IS_MAC) return chord.replace('Mod', 'Ctrl');
  return chord.replace('Mod+', '⌘').replace('Shift+', '⇧').replace('Alt+', '⌥');
}

const ALIGN_ICONS: Record<Alignment, LucideIcon> = {
  left: TextAlignStartIcon,
  center: TextAlignCenterIcon,
  right: TextAlignEndIcon,
  justify: TextAlignJustifyIcon,
};

/** Every focusable control of the toolbar carries this attribute (roving tabindex). */
const ITEM = 'data-toolbar-item';

function ToolbarButton({
  icon: Icon,
  label,
  shortcut,
  pressed,
  expanded,
  controls,
  disabled,
  onClick,
  children,
  ...rest
}: {
  icon: LucideIcon;
  label: string;
  shortcut?: string;
  pressed?: boolean;
  expanded?: boolean;
  controls?: string;
  disabled?: boolean;
  onClick?: () => void;
  children?: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          {...rest}
          {...{ [ITEM]: '' }}
          variant="ghost"
          size={children ? 'sm' : 'icon-sm'}
          aria-label={label}
          aria-pressed={pressed}
          aria-expanded={expanded}
          aria-controls={controls}
          disabled={disabled}
          tabIndex={-1}
          className={cn(pressed && 'bg-accent text-accent-foreground')}
          // Keep the editor selection: buttons must not take focus on mouse down.
          onMouseDown={(event) => {
            event.preventDefault();
          }}
          onClick={onClick}
        >
          <Icon />
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{shortcut ? `${label} (${shortcutLabel(shortcut)})` : label}</TooltipContent>
    </Tooltip>
  );
}

/** A color swatch (mail content color, so an SVG fill instead of a theme token). */
function Swatch({ color }: { color: string | null }) {
  return (
    <svg aria-hidden viewBox="0 0 16 16" className="size-4 shrink-0 rounded-sm border">
      {color ? <rect width="16" height="16" fill={color} /> : <path d="M2 14 14 2" className="stroke-muted-foreground" strokeWidth="1.5" />}
    </svg>
  );
}

function ColorMenu({
  icon,
  label,
  noneLabel,
  colors,
  colorLabels,
  onPick,
}: {
  icon: LucideIcon;
  label: string;
  noneLabel: string;
  colors: readonly { id: ColorId; value: string }[];
  colorLabels: Record<ColorId, string>;
  onPick: (color: string | null) => void;
}) {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <ToolbarButton icon={icon} label={label}>
          <ChevronDownIcon className="size-3" />
        </ToolbarButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" onCloseAutoFocus={(event) => event.preventDefault()}>
        <DropdownMenuItem onSelect={() => onPick(null)}>
          <Swatch color={null} />
          {noneLabel}
        </DropdownMenuItem>
        {colors.map((color) => (
          <DropdownMenuItem key={color.id} onSelect={() => onPick(color.value)}>
            <Swatch color={color.value} />
            {colorLabels[color.id]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Divider() {
  return <span aria-hidden className="mx-0.5 h-5 w-px shrink-0 bg-border" />;
}

/** Attributes of the closest ancestor node (of any of `names`) around the selection, or null outside. */
function ancestorAttrs(editor: Editor, names: readonly string[]): Record<string, unknown> | null {
  const { $from } = editor.state.selection;
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);
    if (names.includes(node.type.name)) return node.attrs;
  }
  return null;
}

const GRID_ROWS = 5;
const GRID_COLS = 6;
const fill = (text: string, values: Record<string, number>) => text.replace(/\{(\w+)\}/g, (match, key: string) => String(values[key] ?? match));

/** "Insert table": a rows x columns grid (arrow keys move, Enter inserts). */
function TableInsertMenu({ editor, labels }: { editor: Editor; labels: RichTextEditorLabels }) {
  const [size, setSize] = useState({ rows: 2, cols: 2 });
  const move = (event: KeyboardEvent<HTMLElement>, rows: number, cols: number) => {
    const delta = { ArrowRight: [0, 1], ArrowLeft: [0, -1], ArrowDown: [1, 0], ArrowUp: [-1, 0] }[event.key];
    if (!delta) return;
    event.preventDefault();
    event.stopPropagation();
    const row = Math.min(GRID_ROWS, Math.max(1, rows + (delta[0] ?? 0)));
    const col = Math.min(GRID_COLS, Math.max(1, cols + (delta[1] ?? 0)));
    event.currentTarget.parentElement?.querySelector<HTMLElement>(`[data-cell="${row}-${col}"]`)?.focus();
  };
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <ToolbarButton icon={Table2Icon} label={labels.insertTable}>
          <ChevronDownIcon className="size-3" />
        </ToolbarButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-auto p-2" onCloseAutoFocus={(event) => event.preventDefault()}>
        <div className="grid grid-cols-6 gap-1">
          {Array.from({ length: GRID_ROWS * GRID_COLS }, (_, index) => {
            const rows = Math.floor(index / GRID_COLS) + 1;
            const cols = (index % GRID_COLS) + 1;
            const selected = rows <= size.rows && cols <= size.cols;
            return (
              <DropdownMenuItem
                key={index}
                data-cell={`${rows}-${cols}`}
                aria-label={fill(labels.tableSize, { rows, cols })}
                className={cn('size-6 justify-center rounded-xs border p-0', selected && 'border-primary bg-primary/20')}
                onPointerEnter={() => setSize({ rows, cols })}
                onFocus={() => setSize({ rows, cols })}
                onKeyDown={(event) => move(event, rows, cols)}
                onSelect={() => editor.chain().focus().insertTable({ rows, cols, withHeaderRow: false }).run()}
              />
            );
          })}
        </div>
        <p className="pt-2 text-center text-xs text-muted-foreground">{fill(labels.tableSize, size)}</p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Table editing, shown while the cursor is inside a table. */
function TableOptionsMenu({
  editor,
  labels,
  borders,
  padding,
  valign,
}: {
  editor: Editor;
  labels: RichTextEditorLabels;
  borders: TableBorders;
  padding: TablePadding;
  valign: CellAlignment;
}) {
  const chain = () => editor.chain().focus();
  const action = (label: string, run: () => void, disabled = false) => (
    <DropdownMenuItem disabled={disabled} onSelect={run}>
      {label}
    </DropdownMenuItem>
  );
  const borderLabels: Record<TableBorders, string> = { none: labels.bordersNone, light: labels.bordersLight, full: labels.bordersFull };
  const paddingLabels: Record<TablePadding, string> = { compact: labels.paddingCompact, normal: labels.paddingNormal };
  const alignLabels: Record<CellAlignment, string> = { top: labels.alignTop, middle: labels.alignMiddle, bottom: labels.alignBottom };
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <ToolbarButton icon={TablePropertiesIcon} label={labels.tableOptions}>
          <ChevronDownIcon className="size-3" />
        </ToolbarButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-96 overflow-y-auto" onCloseAutoFocus={(event) => event.preventDefault()}>
        {action(labels.rowAbove, () => chain().addRowBefore().run())}
        {action(labels.rowBelow, () => chain().addRowAfter().run())}
        {action(labels.deleteRow, () => chain().deleteRow().run())}
        {action(labels.columnBefore, () => chain().addColumnBefore().run())}
        {action(labels.columnAfter, () => chain().addColumnAfter().run())}
        {action(labels.deleteColumn, () => chain().deleteColumn().run())}
        <DropdownMenuSeparator />
        {action(labels.mergeCells, () => chain().mergeCells().run(), !editor.can().mergeCells())}
        {action(labels.splitCell, () => chain().splitCell().run(), !editor.can().splitCell())}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{labels.tableBorders}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={borders} onValueChange={(value) => chain().setTableStyle({ borders: value as TableBorders }).run()}>
          {TABLE_BORDERS.map((value) => (
            <DropdownMenuRadioItem key={value} value={value}>
              {borderLabels[value]}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuLabel>{labels.tablePadding}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={padding} onValueChange={(value) => chain().setTableStyle({ padding: value as TablePadding }).run()}>
          {TABLE_PADDINGS.map((value) => (
            <DropdownMenuRadioItem key={value} value={value}>
              {paddingLabels[value]}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuLabel>{labels.cellAlign}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={valign} onValueChange={(value) => chain().setCellAttribute('valign', value).run()}>
          {CELL_ALIGNMENTS.map((value) => (
            <DropdownMenuRadioItem key={value} value={value}>
              {alignLabels[value]}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        {action(labels.deleteTable, () => chain().deleteTable().run())}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Size and placement of the selected picture. */
function PictureMenu({ editor, labels, width, placement }: { editor: Editor; labels: RichTextEditorLabels; width: number | null; placement: PicturePlacement }) {
  const sizeLabels: Record<PictureSizeId, string> = { small: labels.sizeSmall, medium: labels.sizeMedium, original: labels.sizeOriginal };
  const placementLabels: Record<PicturePlacement, string> = { inline: labels.placementInline, left: labels.placementLeft };
  const sizeId = PICTURE_SIZES.find((size) => size.width === width)?.id ?? '';
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <ToolbarButton icon={ImageIcon} label={labels.pictureOptions}>
          <ChevronDownIcon className="size-3" />
        </ToolbarButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" onCloseAutoFocus={(event) => event.preventDefault()}>
        <DropdownMenuLabel>{labels.pictureSize}</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={sizeId}
          onValueChange={(id) => editor.chain().focus().updateAttributes('image', { width: PICTURE_SIZES.find((size) => size.id === id)?.width ?? null }).run()}
        >
          {PICTURE_SIZES.map((size) => (
            <DropdownMenuRadioItem key={size.id} value={size.id}>
              {sizeLabels[size.id]}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuLabel>{labels.picturePlacement}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={placement} onValueChange={(value) => editor.chain().focus().updateAttributes('image', { placement: value }).run()}>
          {PICTURE_PLACEMENTS.map((value) => (
            <DropdownMenuRadioItem key={value} value={value}>
              {placementLabels[value]}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export interface EditorToolbarProps {
  editor: Editor;
  labels: RichTextEditorLabels;
  onLink: () => void;
  onImage: () => void;
  compactToolbar?: boolean;
  /** Shows the "Plain text" button; omitted when the editor offers its own mode switch. */
  onPlainText?: (() => void) | undefined;
}

export function EditorToolbar({ editor, labels, onLink, onImage, onPlainText, compactToolbar = false }: EditorToolbarProps) {
  const isDesktop = useMediaQuery(BREAKPOINTS.md);
  const compact = compactToolbar && !isDesktop;
  const [expanded, setExpanded] = useState(false);
  const advancedId = useId();
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      underline: e.isActive('underline'),
      strike: e.isActive('strike'),
      bulletList: e.isActive('bulletList'),
      orderedList: e.isActive('orderedList'),
      blockquote: e.isActive('blockquote'),
      link: e.isActive('link'),
      fontSize: (e.getAttributes('textStyle').fontSize as string | undefined) ?? null,
      align: (ALIGNMENTS.find((a) => e.isActive({ textAlign: a })) ?? 'left'),
      table: ancestorAttrs(e, ['table']),
      cell: ancestorAttrs(e, ['tableCell', 'tableHeader']),
      image: e.isActive('image'),
      imageWidth: (e.getAttributes('image').width as number | null | undefined) ?? null,
      imagePlacement: (e.getAttributes('image').placement as PicturePlacement | undefined) ?? 'inline',
      canUndo: e.can().undo(),
      canRedo: e.can().redo(),
    }),
  });
  const chain = () => editor.chain().focus();
  const toolbarRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const [active, setActive] = useState(0);

  // Roving tabindex: exactly one control is tabbable; arrows/Home/End move between them.
  const items = (): HTMLElement[] => Array.from(toolbarRef.current?.querySelectorAll<HTMLElement>(`[${ITEM}]`) ?? []).filter((item) => !item.matches(':disabled') && !item.closest('[hidden],[inert]'));
  const syncTabIndex = (index: number) => {
    const controls = items();
    const current = Math.max(0, Math.min(index, controls.length - 1));
    controls.forEach((item, i) => {
      item.tabIndex = i === current ? 0 : -1;
    });
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const list = items();
    const current = list.indexOf(document.activeElement as HTMLElement);
    if (current < 0) return;
    const next = { ArrowRight: current + 1, ArrowLeft: current - 1, Home: 0, End: list.length - 1 }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    const index = (next + list.length) % list.length;
    setActive(index);
    syncTabIndex(index);
    list[index]?.focus();
  };

  const sizeId = FONT_SIZES.find((size) => size.value === state.fontSize)?.id ?? 'normal';
  const AlignIcon = ALIGN_ICONS[state.align];

  const fontControls = (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <ToolbarButton icon={ALargeSmallIcon} label={labels.fontSize}>
            <ChevronDownIcon className="size-3" />
          </ToolbarButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" onCloseAutoFocus={(event) => event.preventDefault()}>
          <DropdownMenuRadioGroup
            value={sizeId}
            onValueChange={(id) => {
              const size = FONT_SIZES.find((s) => s.id === id)?.value ?? null;
              if (size) chain().setFontSize(size).run();
              else chain().unsetFontSize().run();
            }}
          >
            {FONT_SIZES.map((size) => (
              <DropdownMenuRadioItem key={size.id} value={size.id}>
                {labels.fontSizes[size.id]}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      <Divider />
    </>
  );
  const markControls = (
    <>
      <ToolbarButton icon={StrikethroughIcon} label={labels.strikethrough} shortcut="Mod+Shift+S" pressed={state.strike} onClick={() => chain().toggleStrike().run()} />
      <ColorMenu
        icon={BaselineIcon}
        label={labels.textColor}
        noneLabel={labels.automaticColor}
        colors={TEXT_COLORS}
        colorLabels={labels.colors}
        onPick={(color) => (color ? chain().setColor(color).run() : chain().unsetColor().run())}
      />
      <ColorMenu
        icon={HighlighterIcon}
        label={labels.highlight}
        noneLabel={labels.noHighlight}
        colors={HIGHLIGHT_COLORS}
        colorLabels={labels.colors}
        onPick={(color) => (color ? chain().setHighlight({ color }).run() : chain().unsetHighlight().run())}
      />
      <Divider />
    </>
  );
  const paragraphControls = (
    <>
      <ToolbarButton icon={IndentDecreaseIcon} label={labels.outdent} shortcut="Mod+Shift+M" onClick={() => chain().outdent().run()} />
      <ToolbarButton icon={IndentIncreaseIcon} label={labels.indent} shortcut="Mod+M" onClick={() => chain().indent().run()} />
      <ToolbarButton icon={TextQuoteIcon} label={labels.quote} shortcut="Mod+Shift+B" pressed={state.blockquote} onClick={() => chain().toggleBlockquote().run()} />
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <ToolbarButton icon={AlignIcon} label={labels.align}>
            <ChevronDownIcon className="size-3" />
          </ToolbarButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" onCloseAutoFocus={(event) => event.preventDefault()}>
          <DropdownMenuRadioGroup value={state.align} onValueChange={(value) => chain().setTextAlign(value).run()}>
            {ALIGNMENTS.map((alignment) => {
              const Icon = ALIGN_ICONS[alignment];
              return (
                <DropdownMenuRadioItem key={alignment} value={alignment}>
                  <Icon />
                  {labels.alignments[alignment]}
                </DropdownMenuRadioItem>
              );
            })}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      <Divider />
    </>
  );
  const insertControls = (
    <>
      <ToolbarButton icon={LinkIcon} label={labels.link} shortcut="Mod+K" pressed={state.link} onClick={onLink} />
      <ToolbarButton icon={ImagePlusIcon} label={labels.image} onClick={onImage} />
      {state.image && <PictureMenu editor={editor} labels={labels} width={state.imageWidth} placement={state.imagePlacement} />}
      <TableInsertMenu editor={editor} labels={labels} />
      {state.table && (
        <TableOptionsMenu
          editor={editor}
          labels={labels}
          borders={(state.table.borders as TableBorders | undefined) ?? 'none'}
          padding={(state.table.padding as TablePadding | undefined) ?? 'compact'}
          valign={(state.cell?.valign as CellAlignment | undefined) ?? 'top'}
        />
      )}
      <ToolbarButton icon={SeparatorHorizontalIcon} label={labels.horizontalRule} onClick={() => chain().setHorizontalRule().run()} />
      <ToolbarButton
        icon={RemoveFormattingIcon}
        label={labels.clearFormatting}
        onClick={() => chain().unsetAllMarks().clearNodes().unsetTextAlign().updateAttributes('paragraph', { indent: 0 }).run()}
      />
    </>
  );
  const plainControl = (
    <>
      {onPlainText && (
        <>
          <span className="ml-auto" />
          <ToolbarButton icon={TypeIcon} label={labels.plainText} onClick={onPlainText}>
            <span className="hidden sm:inline">{labels.plainText}</span>
          </ToolbarButton>
        </>
      )}
    </>
  );

  return (
    <div
      ref={(node) => {
        toolbarRef.current = node;
        if (node) {
          syncTabIndex(active);
          if (previousFocus.current && !previousFocus.current.isConnected && document.activeElement === document.body) {
            (node.querySelector<HTMLElement>(`[aria-controls="${advancedId}"]`) ?? items()[0])?.focus();
          }
        }
      }}
      role="toolbar"
      aria-label={labels.toolbar}
      aria-orientation="horizontal"
      onKeyDown={onKeyDown}
      onFocus={(event) => {
        previousFocus.current = event.target;
        const index = items().indexOf(event.target);
        if (index >= 0 && index !== active) {
          setActive(index);
          syncTabIndex(index);
        }
      }}
      onBlur={(event) => {
        if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) previousFocus.current = null;
      }}
      className="flex flex-wrap items-center gap-0.5 border-b bg-muted/40 px-1 py-0.5"
    >
      <ToolbarButton icon={Undo2Icon} label={labels.undo} shortcut="Mod+Z" disabled={!state.canUndo} onClick={() => chain().undo().run()} />
      <ToolbarButton icon={Redo2Icon} label={labels.redo} shortcut="Mod+Y" disabled={!state.canRedo} onClick={() => chain().redo().run()} />
      <Divider />
      {!compact && fontControls}
      <ToolbarButton icon={BoldIcon} label={labels.bold} shortcut="Mod+B" pressed={state.bold} onClick={() => chain().toggleBold().run()} />
      <ToolbarButton icon={ItalicIcon} label={labels.italic} shortcut="Mod+I" pressed={state.italic} onClick={() => chain().toggleItalic().run()} />
      <ToolbarButton icon={UnderlineIcon} label={labels.underline} shortcut="Mod+U" pressed={state.underline} onClick={() => chain().toggleUnderline().run()} />
      {!compact && markControls}
      {compact && <Divider />}
      <ToolbarButton icon={ListIcon} label={labels.bulletList} shortcut="Mod+Shift+8" pressed={state.bulletList} onClick={() => chain().toggleBulletList().run()} />
      <ToolbarButton icon={ListOrderedIcon} label={labels.orderedList} shortcut="Mod+Shift+7" pressed={state.orderedList} onClick={() => chain().toggleOrderedList().run()} />
      {!compact && paragraphControls}
      {!compact && insertControls}
      {!compact && plainControl}
      {compact && <ToolbarButton icon={MoreHorizontalIcon} label={expanded ? labels.lessFormatting : labels.moreFormatting} expanded={expanded} controls={advancedId} onClick={() => {
        if (expanded && document.getElementById(advancedId)?.contains(document.activeElement)) {
          toolbarRef.current?.querySelector<HTMLElement>(`[aria-controls="${advancedId}"]`)?.focus();
        }
        setExpanded(!expanded);
      }} />}
      {compact && <div id={advancedId} hidden={!expanded} role={expanded ? 'group' : undefined} aria-label={expanded ? labels.moreFormatting : undefined} className={expanded ? 'flex w-full flex-wrap items-center gap-0.5' : 'hidden'}>
        {expanded && <>{fontControls}{markControls}{paragraphControls}{insertControls}{plainControl}</>}
      </div>}
    </div>
  );
}
